import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImagePlus, Loader2, X } from "lucide-react";
import { API, api, fmtErr, imgSrc, uploadImage } from "./api";
import { Modal, labelCls, inputCls, btnPrimary, btnGhost } from "./Modal";

const EMPTY = { title: "", client: "", description: "", images: [] };

export const ProjectsTab = () => {
    const [projects, setProjects] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = () =>
        axios.get(`${API}/projects`).then((r) => setProjects(r.data)).catch(() => toast.error("Could not load projects"));
    useEffect(() => {
        load();
    }, []);

    const addPhotos = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const f of files) urls.push(await uploadImage(f));
            setEditing((cur) => ({ ...cur, images: [...cur.images, ...urls] }));
        } catch (er) {
            toast.error(fmtErr(er));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const save = async () => {
        if (!editing.title.trim()) return toast.error("Please type the project title");
        setBusy(true);
        try {
            if (editing.id) {
                await api().put(`/admin/projects/${editing.id}`, editing);
            } else {
                await api().post("/admin/projects", editing);
            }
            toast.success("Saved! Project is live on your website.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete project "${p.title}"?`)) return;
        try {
            await api().delete(`/admin/projects/${p.id}`);
            toast.success("Project deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        }
    };

    return (
        <div data-testid="projects-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-heading font-bold text-2xl text-navy tracking-tight">Completed Projects</h2>
                    <p className="text-sm text-muted mt-1">Real client installations shown in your website's Projects section.</p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} className={btnPrimary} data-testid="add-project-button">
                    <Plus size={18} /> Add New Project
                </button>
            </div>

            {projects.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-line" data-testid="projects-empty">
                    <p className="text-lg font-semibold text-navy">No projects added yet</p>
                    <p className="text-sm text-muted mt-1">Your website currently shows sample projects. Add your real work here.</p>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-list">
                    {projects.map((p) => (
                        <div key={p.id} className="border border-line" data-testid={`project-admin-card-${p.id}`}>
                            {p.images?.[0] ? (
                                <img src={imgSrc(p.images[0])} alt={p.title} className="h-44 w-full object-cover" />
                            ) : (
                                <div className="h-44 bg-bone flex items-center justify-center text-muted text-sm">No photo</div>
                            )}
                            <div className="p-4">
                                <p className="font-heading font-bold text-lg text-navy tracking-tight">{p.title}</p>
                                {p.client && <p className="text-xs text-gold font-mono tracking-widest uppercase mt-1">{p.client}</p>}
                                <p className="text-xs text-muted mt-2">{p.images?.length || 0} photo{(p.images?.length || 0) === 1 ? "" : "s"}</p>
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => setEditing({ ...p })} className="flex-1 inline-flex items-center justify-center gap-2 border border-line py-2.5 text-sm font-semibold hover:border-gold hover:text-gold" data-testid={`edit-project-${p.id}`}>
                                        <Pencil size={14} /> Edit
                                    </button>
                                    <button onClick={() => remove(p)} className="flex-1 inline-flex items-center justify-center gap-2 border border-line py-2.5 text-sm font-semibold hover:border-red-500 hover:text-red-600" data-testid={`delete-project-${p.id}`}>
                                        <Trash2 size={14} /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <Modal title={editing.id ? "Edit Project" : "Add New Project"} onClose={() => setEditing(null)} wide>
                    <div className="grid md:grid-cols-2 gap-5">
                        <div>
                            <label className={labelCls}>Project Title *</label>
                            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                placeholder="e.g. IT Park Office Setup" className={inputCls} data-testid="project-title-input" />
                        </div>
                        <div>
                            <label className={labelCls}>Client / Company Name</label>
                            <input value={editing.client} onChange={(e) => setEditing({ ...editing, client: e.target.value })}
                                placeholder="e.g. ABC Software Pvt Ltd" className={inputCls} data-testid="project-client-input" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>Short Description</label>
                            <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                placeholder="e.g. 200 workstations with mesh chairs, delivered in 3 weeks" className={inputCls} data-testid="project-description-input" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>Project Photos</label>
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                                className="inline-flex items-center gap-2 border-2 border-dashed border-line px-6 py-4 font-semibold text-charcoal hover:border-gold hover:text-gold transition-colors"
                                data-testid="project-upload-button">
                                {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                                {uploading ? "Uploading..." : "Upload Photos (you can pick many at once)"}
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} data-testid="project-file-input" />
                            {editing.images.length > 0 && (
                                <div className="flex flex-wrap gap-3 mt-4" data-testid="project-images-preview">
                                    {editing.images.map((u, i) => (
                                        <div key={i} className="relative">
                                            <img src={imgSrc(u)} alt="" className="h-24 w-24 object-cover border border-line" />
                                            <button type="button"
                                                onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })}
                                                className="absolute -top-2 -right-2 bg-red-600 text-white p-1"
                                                data-testid={`remove-project-image-${i}`} aria-label="Remove photo">
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-3 mt-8">
                        <button onClick={save} disabled={busy} className={btnPrimary} data-testid="save-project-button">
                            {busy ? "Saving..." : "Save Project"}
                        </button>
                        <button onClick={() => setEditing(null)} className={btnGhost} data-testid="cancel-project-button">Cancel</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};
